import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { socket } from "./socket";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { lazyWithRetry, clearChunkReloadFlag } from "./utils/lazyWithRetry";

import HomeNavbar from "./components/HomeNavbar";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingScreen from "./components/LoadingScreen";
import OfflineBanner from "./components/OfflineBanner";

import API from "./api";

// Lazy Loaded Pages
const Home = lazyWithRetry(() => import("./pages/Home"));
const About = lazyWithRetry(() => import("./pages/About"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const NonDiscrimination = lazyWithRetry(() => import("./pages/NonDiscrimination"));
const Consent = lazyWithRetry(() => import("./pages/Consent"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const InsurancePlans = lazyWithRetry(() => import("./pages/InsurancePlans"));

// Auth & Setup
const Signup = lazyWithRetry(() => import("./components/Signup"));
const Login = lazyWithRetry(() => import("./components/Login"));
const DoctorLogin = lazyWithRetry(() => import("./components/DoctorLogin"));
const ForgotPassword = lazyWithRetry(() => import("./components/ForgotPassword"));
const CompleteProfile = lazyWithRetry(() => import("./components/CompleteProfile"));
const RegisterDoctor = lazyWithRetry(() => import("./pages/RegisterDoctor"));
const PharmacySignup = lazyWithRetry(() => import("./pages/PharmacySignup"));
const PharmacyLogin = lazyWithRetry(() => import("./pages/PharmacyLogin"));
const InsurerLogin = lazyWithRetry(() => import("./pages/InsurerLogin"));
const InsurerSignup = lazyWithRetry(() => import("./pages/InsurerSignup"));
const AdminLogin = lazyWithRetry(() => import("./pages/AdminLogin"));

// Public Entities
const Doctors = lazyWithRetry(() => import("./pages/Doctors"));
const DoctorProfile = lazyWithRetry(() => import("./pages/DoctorProfile"));

// Dashboards
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const DoctorDashboard = lazyWithRetry(() => import("./pages/DoctorDashboard"));
const PharmacyDashboard = lazyWithRetry(() => import("./pages/PharmacyDashboard"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const InsurerDashboard = lazyWithRetry(() => import("./pages/InsurerDashboard"));

// Profile
const ProfileCompletionPage = lazyWithRetry(() => import("./pages/ProfileCompletionPage"));
const PatientProfile = lazyWithRetry(() => import("./pages/PatientProfile"));

// EHR & Records
const PatientRecords = lazyWithRetry(() => import("./pages/PatientRecords"));
const PatientEHR = lazyWithRetry(() => import("./pages/PatientEHR"));
const PatientPrescriptions = lazyWithRetry(() => import("./pages/PatientPrescriptions"));
const DoctorEHRView = lazyWithRetry(() => import("./pages/DoctorEHRView"));
const DoctorEHREdit = lazyWithRetry(() => import("./pages/DoctorEHREdit"));

// Appointments & Video
const MyAppointments = lazyWithRetry(() => import("./pages/MyAppointments"));
const DoctorAppointments = lazyWithRetry(() => import("./pages/DoctorAppointments"));
const DoctorPatients = lazyWithRetry(() => import("./pages/DoctorPatients"));
const VideoRoom = lazyWithRetry(() => import("./pages/VideoRoom"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));

// Admin
const AdminVerifications = lazyWithRetry(() => import("./pages/AdminVerifications"));
const AdminAuditLogs = lazyWithRetry(() => import("./pages/AdminAuditLogs"));
const AdminUsers = lazyWithRetry(() => import("./pages/AdminUsers"));
const AdminAppointments = lazyWithRetry(() => import("./pages/AdminAppointments"));
const AdminAnnouncements = lazyWithRetry(() => import("./pages/AdminAnnouncements"));

// Pharmacy
const PharmacyPrescriptions = lazyWithRetry(() => import("./pages/PharmacyPrescriptions"));
const PharmacyPrescriptionDetails = lazyWithRetry(() => import("./pages/PharmacyPrescriptionDetails"));
const PharmacyInventory = lazyWithRetry(() => import("./pages/PharmacyInventory"));

// Billing & Claims
const DoctorBilling = lazyWithRetry(() => import("./pages/DoctorBilling"));
const PatientBills = lazyWithRetry(() => import("./pages/PatientBills"));
const PatientClaims = lazyWithRetry(() => import("./pages/PatientClaims"));
const InsurerClaims = lazyWithRetry(() => import("./pages/InsurerClaims"));
const InsurerClaimDetails = lazyWithRetry(() => import("./pages/InsurerClaimDetails"));
const InsuredMembers = lazyWithRetry(() => import("./pages/InsuredMembers"));
const InsuredMemberDetails = lazyWithRetry(() => import("./pages/InsuredMemberDetails"));

function RoleDashboardRedirect({ user }) {
  if (!user) return <Navigate to="/login" replace />;

  if (user.role === "admin") return <Navigate to="/dashboard/admin" replace />;
  if (user.role === "doctor") return <DoctorDashboard />;
  if (user.role === "pharmacy") return <Navigate to="/dashboard/pharmacy" replace />;
  if (user.role === "insurer") return <Navigate to="/dashboard/insurer" replace />;

  return <Dashboard user={user} />;
}

function AppInner() {
  const { user, setUser, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // App mounted successfully → re-arm reload-on-next-deploy for stale chunks.
    // (Auth bootstrap + socket connect now live in AuthContext.)
    clearChunkReloadFlag();
  }, []);

  const isPublicPage = !location.pathname.startsWith("/dashboard");
  const authRoutes = ["/login", "/signup", "/doctor-login", "/register-doctor", "/pharmacy-login", "/pharmacy-signup", "/insurer-login", "/insurer-signup", "/admin-login", "/forgot-password", "/reset-password"];
  const isExcludedPage = authRoutes.some(route => location.pathname.startsWith(route));

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <OfflineBanner />
      {isPublicPage && !isExcludedPage && <HomeNavbar user={user} setUser={setUser} />}

      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<Home user={user} setUser={setUser} />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/non-discrimination" element={<NonDiscrimination />} />
          <Route path="/consent" element={<Consent />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/signup" element={<Signup setUser={setUser} />} />
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/doctor-login" element={<DoctorLogin user={user} setUser={setUser} />} />
          <Route path="/register-doctor" element={<RegisterDoctor setUser={setUser} />} />
          <Route path="/pharmacy-signup" element={<PharmacySignup setUser={setUser} />} />
          <Route path="/pharmacy-login" element={<PharmacyLogin user={user} setUser={setUser} />} />
          <Route path="/insurer-login" element={<InsurerLogin user={user} setUser={setUser} />} />
          <Route path="/insurer-signup" element={<InsurerSignup setUser={setUser} />} />
          <Route path="/admin-login" element={<AdminLogin user={user} setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route path="/insurance-plans" element={<InsurancePlans />} />

          <Route path="/doctors" element={<Doctors />} />
          <Route path="/doctor/:id" element={<DoctorProfile />} />

          <Route element={<AppLayout user={user} setUser={setUser} />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute user={user}>
                  <RoleDashboardRedirect user={user} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/profile"
              element={
                <ProtectedRoute user={user}>
                  <ProfileCompletionPage user={user} setUser={setUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/doctor/profile"
              element={
                <ProtectedRoute user={user} allowedRoles={["doctor"]}>
                  <ProfileCompletionPage user={user} setUser={setUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/pharmacy/profile"
              element={
                <ProtectedRoute user={user} allowedRoles={["pharmacy"]}>
                  <ProfileCompletionPage user={user} setUser={setUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/insurer/profile"
              element={
                <ProtectedRoute user={user} allowedRoles={["insurer"]}>
                  <ProfileCompletionPage user={user} setUser={setUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin/profile"
              element={
                <ProtectedRoute user={user} allowedRoles={["admin"]}>
                  <ProfileCompletionPage user={user} setUser={setUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/doctors"
              element={
                <ProtectedRoute user={user}>
                  <Doctors />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/records"
              element={
                <ProtectedRoute user={user}>
                  <PatientRecords user={user} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/ehr"
              element={
                <ProtectedRoute user={user}>
                  <PatientEHR />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/prescriptions"
              element={
                <ProtectedRoute user={user} allowedRoles={["patient"]}>
                  <PatientPrescriptions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/doctor/patient/:id/ehr"
              element={
                <ProtectedRoute user={user} allowedRoles={["doctor"]}>
                  <DoctorEHRView />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/doctor/patient/:id/ehr/add"
              element={
                <ProtectedRoute user={user} allowedRoles={["doctor"]}>
                  <DoctorEHREdit />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/appointments"
              element={
                <ProtectedRoute user={user} allowedRoles={["patient"]}>
                  <MyAppointments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/doctor/appointments"
              element={
                <ProtectedRoute user={user} allowedRoles={["doctor"]}>
                  <DoctorAppointments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/patients"
              element={
                <ProtectedRoute user={user} allowedRoles={["doctor"]}>
                  <DoctorPatients user={user} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/patient/:id"
              element={
                <ProtectedRoute user={user} allowedRoles={["patient"]}>
                  <PatientProfile user={user} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/appointments/:id/video"
              element={
                <ProtectedRoute user={user} allowedRoles={["patient"]}>
                  <VideoRoom />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/doctor/appointments/:id/video"
              element={
                <ProtectedRoute user={user} allowedRoles={["doctor"]}>
                  <VideoRoom />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/messages"
              element={
                <ProtectedRoute user={user} allowedRoles={["patient", "doctor"]}>
                  <Messages />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/pharmacy"
              element={
                <ProtectedRoute user={user} allowedRoles={["pharmacy"]}>
                  <PharmacyDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/pharmacy/prescriptions"
              element={
                <ProtectedRoute user={user} allowedRoles={["pharmacy"]} requireProfileComplete>
                  <PharmacyPrescriptions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/pharmacy/prescriptions/:id"
              element={
                <ProtectedRoute user={user} allowedRoles={["pharmacy"]} requireProfileComplete>
                  <PharmacyPrescriptionDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/pharmacy/inventory"
              element={
                <ProtectedRoute user={user} allowedRoles={["pharmacy"]} requireProfileComplete>
                  <PharmacyInventory />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/doctor/billing"
              element={
                <ProtectedRoute user={user} allowedRoles={["doctor"]}>
                  <DoctorBilling />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/bills"
              element={
                <ProtectedRoute user={user} allowedRoles={["patient"]}>
                  <PatientBills />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/claims"
              element={
                <ProtectedRoute user={user} allowedRoles={["patient"]}>
                  <PatientClaims />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin"
              element={
                <ProtectedRoute user={user} allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin/verifications"
              element={
                <ProtectedRoute user={user} allowedRoles={["admin"]}>
                  <AdminVerifications />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin/audit-logs"
              element={
                <ProtectedRoute user={user} allowedRoles={["admin"]}>
                  <AdminAuditLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin/users"
              element={
                <ProtectedRoute user={user} allowedRoles={["admin"]}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin/appointments"
              element={
                <ProtectedRoute user={user} allowedRoles={["admin"]}>
                  <AdminAppointments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin/announcements"
              element={
                <ProtectedRoute user={user} allowedRoles={["admin"]}>
                  <AdminAnnouncements />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/insurer"
              element={
                <ProtectedRoute user={user} allowedRoles={["insurer"]}>
                  <InsurerDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/insurer/claims"
              element={
                <ProtectedRoute user={user} allowedRoles={["insurer"]}>
                  <InsurerClaims />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/insurer/claims/:id"
              element={
                <ProtectedRoute user={user} allowedRoles={["insurer"]}>
                  <InsurerClaimDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/insurer/members"
              element={
                <ProtectedRoute user={user} allowedRoles={["insurer"]}>
                  <InsuredMembers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/insurer/members/:id"
              element={
                <ProtectedRoute user={user} allowedRoles={["insurer"]}>
                  <InsuredMemberDetails />
                </ProtectedRoute>
              }
            />

          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <ErrorBoundary>
          <AppInner />
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}